/**
 * Just Be: Soft Power™ — Costa Rica 2026 (Archived Retrospective)
 * Storytelling archive of the June 2026 retreat. Booking engine, pricing,
 * room inventory and inclusions were removed — the permanent hub lives at /retreats.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Sparkles, Heart, Users, ChevronRight, Sun, Moon, Sunrise,
  Waves, Leaf, Quote, Play, ArrowLeft, ArrowRight, Palmtree
} from 'lucide-react';

// Host images
const HOST_IMAGES = {
  crystalAndNicole: 'https://customer-assets.emergentagent.com/job_volition-membership/artifacts/wdy6rj8w_crystal%20and%20drnicole.png',
};

// Retreat images
const RETREAT_IMAGES = {
  hero: 'https://customer-assets-jai6qajn.emergentagent.net/job_76c777d1-11b9-4899-8be6-0aa56b6fa5a1/artifacts/3x6hklgc_DSC00077.JPG',
  costaRica1: 'https://i.ontraport.com/260712.155544a5758d1d35b09cacdf90ad353f.PNG',
  costaRica2: 'https://i.ontraport.com/260712.24d51fd5238aaff0d1e8dc4705bba054.PNG',
  costaRica3: 'https://i.ontraport.com/260712.e8246cf334e8430fccff91d4d75d391c.PNG',
  costaRica4: 'https://customer-assets-jai6qajn.emergentagent.net/job_76c777d1-11b9-4899-8be6-0aa56b6fa5a1/artifacts/yfvfq13e_IMG_1853.webp',
};

// Video testimonials
const VIDEO_TESTIMONIALS = [
  { vimeoId: "708002728" },
  { vimeoId: "1082666575" },
  { vimeoId: "708745464" },
  { vimeoId: "746901729" },
  { vimeoId: "746901578" },
  { vimeoId: "746901352" },
  { vimeoId: "708002473" },
  { vimeoId: "708002538" },
  { vimeoId: "708002647" },
  { vimeoId: "708002791" },
];

// Costa Rica travel FAQs (archived reference)
const FAQS = [
  {
    question: "Where did attendees fly into Costa Rica?",
    answer: "San Jose (Juan Santamaria) International Airport (SJO). From there, the group boarded a private charter jet to Quepos — a 25-minute flight included in the retreat experience."
  },
  {
    question: "What did the journey to Imiloa look like?",
    answer: "A private charter jet from SJO to Quepos (25 minutes), followed by ground transportation to the Imiloa Institute campus, tucked into the Costa Rican rainforest."
  },
  {
    question: "What did sisters pack?",
    answer: "Sunglasses, beach hat, organic sunscreen & mosquito repellent, comfortable closed-toe shoes for jungle trails, sandals, swimwear, yoga attire, a flashlight, reusable water bottle, and electrolytes."
  },
  {
    question: "Was there WiFi at Imiloa?",
    answer: "Yes, WiFi was available on campus, with WhatsApp recommended for communication. Standard North American power outlets were used."
  },
  {
    question: "Was this retreat open to men?",
    answer: "No — Just Be experiences are designed for and catered to the elevation and wellness of women."
  },
  {
    question: "How physically demanding was the property?",
    answer: "While not strenuous for most guests, the Imiloa campus involves moderate walking, including stairs and hilly areas surrounded by rainforest."
  }
];

const RetreatLandingPage = () => {
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);
  const [showAllTestimonials, setShowAllTestimonials] = useState(false);

  useEffect(() => {
    document.title = 'Just Be: Soft Power™ — Costa Rica 2026 | Sister Diamonds Retreats';
    window.scrollTo(0, 0);
    const img = new Image();
    img.src = RETREAT_IMAGES.hero;
    img.onload = () => setHeroImageLoaded(true);
  }, []);

  return (
    <div className="min-h-screen bg-cream" data-testid="costa-rica-archive-page">
      {/* Retrospective Banner */}
      <div className="bg-terracotta text-white text-center text-sm py-2 px-4" data-testid="retrospective-banner">
        You&apos;re viewing a past retreat — Just Be: Soft Power™, Costa Rica 2026.{' '}
        <Link to="/retreats" className="underline font-medium hover:text-white/80">Explore all Sister Diamonds Retreats →</Link>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-forest via-forest/80 to-terracotta/60" />
        <div
          className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${heroImageLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{ backgroundImage: `url(${RETREAT_IMAGES.hero})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-forest/70 via-forest/15 to-forest/10" />
        </div>

        <div className="absolute top-6 left-6 z-20">
          <Link to="/retreats" className="inline-flex items-center gap-2 text-white/90 hover:text-white transition-colors bg-black/20 px-4 py-2 rounded-full backdrop-blur-sm">
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium">All Retreats</span>
          </Link>
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto mt-24">
          <div className="bg-forest/60 backdrop-blur-md rounded-3xl px-6 sm:px-12 py-10 shadow-2xl">
          <p className="inline-block bg-forest/80 text-white font-semibold tracking-widest uppercase mb-4 text-sm px-4 py-2 rounded-full">
            A Sister Diamonds Retrospective • Imiloa Institute, Costa Rica • June 26 – July 1, 2026
          </p>
          <h1 className="font-heading text-5xl md:text-7xl font-bold text-white mb-6 leading-tight drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]">
            Just Be: <br />
            <span className="text-terracotta drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">Soft Power</span>™ Retreat
          </h1>
          <p className="text-xl md:text-2xl text-white mb-8 max-w-2xl mx-auto leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.7)]">
            Six days. 18 women. One invitation: stop striving and experience the power of softness.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/retreats#priority-list">
              <Button size="lg" className="bg-terracotta hover:bg-terracotta/90 text-white px-8 py-6 text-lg shadow-xl" data-testid="archive-hero-priority-btn">
                Be First to Know About the Next Retreat
              </Button>
            </Link>
            <Link to="/retreats">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg shadow-xl" data-testid="archive-hero-all-retreats-btn">
                Explore All Retreats
              </Button>
            </Link>
          </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronRight className="h-8 w-8 text-white rotate-90" />
        </div>
      </section>

      {/* Navigation Bar (sticky) */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-sand shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/retreats" className="font-heading text-xl font-bold text-forest">
              Sister Diamonds <span className="hidden sm:inline text-terracotta text-sm font-medium">Retreats</span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <a href="#about" className="text-gray-600 hover:text-forest transition">About</a>
              <a href="#experience" className="text-gray-600 hover:text-forest transition">Experience</a>
              <a href="#hosts" className="text-gray-600 hover:text-forest transition">Hosts</a>
              <a href="#faq" className="text-gray-600 hover:text-forest transition">FAQ</a>
            </div>
            <Link to="/retreats">
              <Button size="sm" className="bg-forest text-white hover:bg-forest/90">All Retreats</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* About Section */}
      <section id="about" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-terracotta/10 text-terracotta mb-4">For High-Capacity Women</Badge>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-forest mb-6">
                You Don&apos;t Need Another Checklist Disguised as Self-Care
              </h2>
              <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
                <p>
                  You&apos;ve done enough. You&apos;ve built the life, the brand, the legacy—but deep down, you&apos;re quietly asking: <em>&quot;Where do I go to be poured into without performing?&quot;</em>
                </p>
                <p>
                  This retreat was for the woman who felt the shift coming but needed space to hear what God was saying. For the woman who was tired of being strong every damn day, and ready to remember what softness feels like.
                </p>
                <p className="font-semibold text-forest">
                  This was not a reward for doing more.<br />
                  This was her permission to Just Be.
                </p>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-sand/30 rounded-xl">
                  <Heart className="h-6 w-6 text-terracotta" />
                  <span className="font-medium text-forest">Soul-Deep Rest</span>
                </div>
                <div className="flex items-center gap-3 p-4 bg-sand/30 rounded-xl">
                  <Users className="h-6 w-6 text-terracotta" />
                  <span className="font-medium text-forest">Sacred Sisterhood</span>
                </div>
                <div className="flex items-center gap-3 p-4 bg-sand/30 rounded-xl">
                  <Sparkles className="h-6 w-6 text-terracotta" />
                  <span className="font-medium text-forest">Transformation</span>
                </div>
                <div className="flex items-center gap-3 p-4 bg-sand/30 rounded-xl">
                  <Palmtree className="h-6 w-6 text-terracotta" />
                  <span className="font-medium text-forest">Luxury Setting</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <img src={RETREAT_IMAGES.costaRica1} alt="Costa Rica retreat" className="rounded-2xl h-64 w-full object-cover" loading="lazy" />
              <img src={RETREAT_IMAGES.costaRica2} alt="Costa Rica nature" className="rounded-2xl h-64 w-full object-cover mt-8" loading="lazy" />
              <img src={RETREAT_IMAGES.costaRica3} alt="Luxury accommodation" className="rounded-2xl h-64 w-full object-cover -mt-8" loading="lazy" />
              <img src={RETREAT_IMAGES.costaRica4} alt="River clay ritual" className="rounded-2xl h-64 w-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Not a spa escape */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://i.ontraport.com/260712.726717efdfdf89ec33f8e110c365b47c.PNG"
                alt="Crystal and Dr. Nicole under flower arch"
                className="rounded-2xl shadow-2xl w-full"
                loading="lazy"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-forest mb-6 leading-tight">
                <span className="text-terracotta italic">Just Be:</span> The Soft Power Retreat was <span className="underline decoration-terracotta/30">not</span> a spa escape.
              </h2>
              <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
                <p>It was <em>not a curated getaway</em> for staged photos and surface-level healing.</p>
                <p>It was <em>not a business trip in disguise</em>—and it was <em>not a reward for doing more.</em></p>
              </div>
              <div className="mt-8 p-6 bg-cream rounded-2xl border-l-4 border-terracotta">
                <p className="text-forest font-heading text-xl font-medium leading-relaxed">
                  This retreat was created for the woman who&apos;s built the life, the brand, the legacy— but deep down, was quietly asking:
                </p>
                <p className="text-terracotta italic text-2xl font-heading mt-4">
                  &quot;Where do I go to be poured into without performing?&quot;
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* A Day in the Life */}
      <section id="experience" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-terracotta/10 text-terracotta mb-4">The Experience</Badge>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-forest mb-4">
              A Day in the Life at Just Be
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Every moment was curated to restore your rhythm, not control it. No rush. No pressure to perform.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="card-base border-t-4 border-t-yellow-400">
              <CardContent className="p-6">
                <Sunrise className="h-10 w-10 text-yellow-500 mb-4" />
                <h3 className="font-heading text-xl font-bold text-forest mb-2">Morning</h3>
                <p className="text-sm text-gray-500 mb-3">Stillness + Soul</p>
                <p className="text-gray-600">
                  Wake to birdsong. Nourishing breakfast. Soul-stirring sessions with Crystal &amp; Dr. Nicole. Begin grounded and centered.
                </p>
              </CardContent>
            </Card>

            <Card className="card-base border-t-4 border-t-blue-400">
              <CardContent className="p-6">
                <Sun className="h-10 w-10 text-blue-500 mb-4" />
                <h3 className="font-heading text-xl font-bold text-forest mb-2">Midday</h3>
                <p className="text-sm text-gray-500 mb-3">Rest + Reconnection</p>
                <p className="text-gray-600">
                  Nap. Walk the jungle path. Swim beneath the waterfall. Long, laughter-filled lunch. Rest isn&apos;t earned—it&apos;s expected.
                </p>
              </CardContent>
            </Card>

            <Card className="card-base border-t-4 border-t-orange-400">
              <CardContent className="p-6">
                <Sparkles className="h-10 w-10 text-orange-500 mb-4" />
                <h3 className="font-heading text-xl font-bold text-forest mb-2">Afternoon</h3>
                <p className="text-sm text-gray-500 mb-3">Unmasking + Breakthroughs</p>
                <p className="text-gray-600">
                  Enter sacred space. Speak truths you&apos;ve never had space to say. Discover strength in softness.
                </p>
              </CardContent>
            </Card>

            <Card className="card-base border-t-4 border-t-purple-400">
              <CardContent className="p-6">
                <Moon className="h-10 w-10 text-purple-500 mb-4" />
                <h3 className="font-heading text-xl font-bold text-forest mb-2">Evening</h3>
                <p className="text-sm text-gray-500 mb-3">Ritual + Restoration</p>
                <p className="text-gray-600">
                  Candlelit dinners. Laughter. Tears. Easier breathing. Feel more you than you have in years.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Who This Was For */}
      <section className="py-20 px-6 bg-forest text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-8">
            It was for the woman who...
          </h2>
          <div className="space-y-6 text-lg md:text-xl text-white/90 leading-relaxed">
            <p>...felt the shift coming but needed space to hear what God was saying.</p>
            <p className="font-medium text-white">...was tired of being strong every damn day, and ready to remember what softness feels like.</p>
            <p>...was ready to stop striving and start surrendering—without losing her power.</p>
            <p>...had checked every box, carried every load, and was quietly wondering—<em>&quot;Is this all there is?&quot;</em></p>
          </div>
        </div>
      </section>

      {/* Permission to Just Be */}
      <section className="py-20 px-6 bg-gradient-to-b from-cream to-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-terracotta/10 text-terracotta mb-4">The Permission Slip</Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-forest mb-4">
                This was not a reward for doing more.
              </h2>
              <p className="text-terracotta text-2xl font-heading italic mb-8">
                This was permission to Just Be.
              </p>
              <p className="text-gray-700 text-lg mb-8">
                Over six sacred days in the lush heart of Costa Rica, women were guided to:
              </p>
              <div className="space-y-4">
                {[
                  { action: 'Release', description: "what's been silently draining you." },
                  { action: 'Reclaim', description: "your voice, boundaries, and joy." },
                  { action: 'Reconnect', description: "with your body, calling, and your God." },
                  { action: 'Rise', description: "in your softness, not in your stress." }
                ].map((item, i) => (
                  <div key={item.action} className="flex items-start gap-4 bg-white rounded-xl p-4 shadow-sm border border-forest/10">
                    <div className="h-10 w-10 bg-terracotta text-white rounded-full flex items-center justify-center flex-shrink-0 font-heading font-bold">
                      {i + 1}
                    </div>
                    <div>
                      <span className="font-heading text-forest font-bold text-lg">{item.action}</span>
                      <span className="text-gray-600 ml-2">{item.description}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src="https://customer-assets-jai6qajn.emergentagent.net/job_76c777d1-11b9-4899-8be6-0aa56b6fa5a1/artifacts/c13z5zha_IMG_1898.webp"
                alt="Morning movement in the open-air pavilion"
                className="rounded-2xl shadow-2xl w-full"
                loading="lazy"
              />
              <div className="mt-6 bg-forest text-white rounded-xl p-6 text-center">
                <p className="text-xl font-heading">
                  Surrounded by women who don&apos;t need your résumé—<span className="text-terracotta">only your realness.</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rest Is The Return - Quote Section */}
      <section className="py-16 px-6 bg-terracotta text-white text-center">
        <div className="max-w-3xl mx-auto">
          <Quote className="h-12 w-12 mx-auto mb-6 opacity-50" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Rest is Not the Retreat.
          </h2>
          <p className="text-2xl md:text-3xl font-heading italic">
            Rest is the Return!
          </p>
        </div>
      </section>

      {/* Imiloa Sanctuary Video */}
      <section className="py-16 px-6 bg-gradient-to-b from-white to-sand/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="bg-forest/10 text-forest mb-4">Where the Sisters Stayed</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-forest mb-4">
              The Sanctuary at Imiloa
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
              From the treetop Bali-style suites to the ocean-view bungalows, every room was intentionally designed to nourish body, mind, and spirit — rising with the sun on a private terrace, winding down beneath a starlit sky.
            </p>
          </div>

          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black">
            <div className="aspect-video">
              <iframe
                src="https://player.vimeo.com/video/1167876587?badge=0&autopause=0&player_id=0&app_id=58479"
                className="w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                allowFullScreen
                title="Imiloa Accommodations"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-10">
            <div className="text-center">
              <div className="h-12 w-12 bg-forest/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Sun className="h-6 w-6 text-forest" />
              </div>
              <p className="text-sm text-gray-700 font-medium">Private Terraces</p>
            </div>
            <div className="text-center">
              <div className="h-12 w-12 bg-forest/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Waves className="h-6 w-6 text-forest" />
              </div>
              <p className="text-sm text-gray-700 font-medium">Ocean Views</p>
            </div>
            <div className="text-center">
              <div className="h-12 w-12 bg-forest/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Leaf className="h-6 w-6 text-forest" />
              </div>
              <p className="text-sm text-gray-700 font-medium">Jungle Immersion</p>
            </div>
            <div className="text-center">
              <div className="h-12 w-12 bg-forest/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Moon className="h-6 w-6 text-forest" />
              </div>
              <p className="text-sm text-gray-700 font-medium">Starlit Nights</p>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Your Hosts */}
      <section id="hosts" className="py-20 px-6 bg-forest text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-white/20 text-white mb-4">Your Guides</Badge>
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Meet Your Hosts
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="flex justify-center">
              <div className="rounded-2xl overflow-hidden border-4 border-terracotta shadow-2xl max-w-md">
                <img
                  src={HOST_IMAGES.crystalAndNicole}
                  alt="Crystal Khalil & Dr. Nicole LaBeach"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <h3 className="font-heading text-2xl font-bold mb-2">Crystal Khalil</h3>
                <p className="text-terracotta font-medium mb-3">The Divine Disruptor &amp; Destiny Dealer</p>
                <p className="text-white/80 leading-relaxed">
                  The strategist with soul, the executive whisperer. From boardrooms to Bible study, she moves with purpose and power. With 30+ years of corporate savvy and a heart for service, she&apos;s built a movement through Sister Diamonds and Volition Enterprises.
                </p>
              </div>

              <div className="border-t border-white/20 pt-8">
                <h3 className="font-heading text-2xl font-bold mb-2">Dr. Nicole LaBeach</h3>
                <p className="text-terracotta font-medium mb-3">The Soul Surgeon &amp; Sacred Strategist</p>
                <p className="text-white/80 leading-relaxed">
                  With a Ph.D. in psychology and 25+ years of experience, Dr. Nicole transforms lives. Featured on GMA, CBS Radio, Oprah Daily, Essence, and as the 3-season host of &quot;Put A Ring On It&quot; on OWN. She holds space for you to remember who you were before the world told you who to be.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Testimonials Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-cream to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-terracotta/10 text-terracotta mb-4">Testimonials</Badge>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-forest mb-4">
              Hear From Our Sisters
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Real transformation stories from women who&apos;ve experienced our coaching and retreats.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-10 mb-12 justify-items-center">
            {VIDEO_TESTIMONIALS.slice(0, showAllTestimonials ? VIDEO_TESTIMONIALS.length : 3).map((testimonial, index) => (
              <div key={testimonial.vimeoId} className="group relative" data-testid={`video-testimonial-${index}`}>
                <div className="relative bg-gradient-to-b from-gray-800 via-gray-900 to-black rounded-[2.5rem] p-2 shadow-2xl transform transition-all duration-500 hover:scale-105">
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-24 h-6 bg-black rounded-b-2xl z-10 flex items-center justify-center">
                    <div className="w-12 h-3 bg-gray-800 rounded-full"></div>
                  </div>
                  <div className="relative bg-black rounded-[2rem] overflow-hidden" style={{ width: '200px', height: '380px' }}>
                    <iframe
                      src={`https://player.vimeo.com/video/${testimonial.vimeoId}?h=&title=0&byline=0&portrait=0&background=0`}
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ transform: 'scale(1.02)' }}
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      title={`Testimonial ${index + 1}`}
                    ></iframe>
                  </div>
                  <div className="flex justify-center py-2">
                    <div className="w-24 h-1 bg-gray-600 rounded-full"></div>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <p className="text-sm font-medium text-forest flex items-center justify-center gap-2">
                    <Play className="h-3 w-3 text-terracotta" />
                    Sister Diamond Story
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button
              variant="outline"
              onClick={() => setShowAllTestimonials(!showAllTestimonials)}
              className="border-2 border-forest text-forest hover:bg-forest hover:text-white transition-all duration-300 px-8 py-3 rounded-full font-medium"
              data-testid="toggle-testimonials-btn"
            >
              {showAllTestimonials ? 'Show Less' : `View More Stories (${VIDEO_TESTIMONIALS.length - 3} more)`}
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-terracotta/10 text-terracotta mb-4">Costa Rica 2026 Archive</Badge>
            <h2 className="font-heading text-4xl font-bold text-forest">
              About This Retreat
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {FAQS.map((faq, i) => (
              <AccordionItem key={faq.question} value={`faq-${i}`} className="bg-white rounded-xl border border-sand px-6">
                <AccordionTrigger className="font-heading text-lg text-forest hover:no-underline py-4 text-left">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-forest text-white text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
            The Next Chapter Is <span className="text-terracotta">Being Written.</span>
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Costa Rica was one chapter in the Just Be™ story. Be among the first to know where we&apos;ll gather next.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/retreats#priority-list">
              <Button size="lg" className="bg-terracotta hover:bg-terracotta/90 text-white px-8 py-6 text-lg" data-testid="archive-final-priority-btn">
                Join the Retreat Priority List <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/retreats">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Explore All Retreats
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-forest/95 text-white/60 text-center text-sm">
        <p>©2026 Sister Diamonds, LLC. All Rights Reserved.</p>
        <p className="mt-2">
          Questions? Contact us at <a href="mailto:info@sisterdiamonds.com" className="text-terracotta hover:underline">info@sisterdiamonds.com</a>
        </p>
      </footer>
    </div>
  );
};

export default RetreatLandingPage;
